import React from 'react'
import { useFirebase } from './FirebaseProvider'
import { user } from 'rxfire/auth'
import { User } from '../logic/User'
import { User as FirebaseUser, signInAnonymously, signOut, updateEmail, linkWithCredential, EmailAuthProvider } from 'firebase/auth'
import { UserClaim } from '../types/interfaces'
import { Observable, shareReplay, switchMap } from 'rxjs'
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router'
import { loginURL } from 'routes/urls'
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'

interface IAuthUserContext {
  authUser?: User | null
  refreshUser: (forceRefresh?: boolean) => Promise<User | null>
  logout: () => Promise<void>
  signInAsGuest: (email: string) => Promise<User | null>
  linkCredentials: (email: string, password: string) => Promise<User | null>
  authUser$: Observable<User | null>
}

const AuthUserContext = React.createContext<IAuthUserContext>({} as IAuthUserContext)

const AuthUserProvider: React.FC = ({ children }) => {
  const { auth } = useFirebase()
  const [authUser, setAuthUser] = React.useState<User | null>()
  const navigate = useNavigate()
  const sendDataToGTM = useGTMDispatch()

  // get User from firebase IndexedDB
  const getUser = async (firebaseUser: FirebaseUser | null, forceRefresh?: boolean) => {
    if (firebaseUser) {
      const tokenResult = await firebaseUser.getIdTokenResult(forceRefresh)
      const expiry = DateTime.fromRFC2822(tokenResult.expirationTime)
      if (expiry < DateTime.now()) {
        console.log(`authToken expired at ${expiry.toISO()}`)
        return null
      } else {
        return new User(firebaseUser, tokenResult?.claims as UserClaim)
      }
    } else {
      console.log('AuthUserProvider.getUser() firebaseUser is false', firebaseUser)
      return null
    }
  }

  // sync context User with firebase IndexedDB
  const refreshUser = async (forceRefresh?: boolean) => {
    const authUser = await getUser(auth.currentUser, forceRefresh)
    setAuthUser(authUser)
    return authUser
  }

  const logout = async () => {
    const firebaseId = authUser?.firebaseId
    await signOut(auth)
    await refreshUser(true)
    // send GTM event when user is unauthenticated
    if (firebaseId) sendDataToGTM({ event: 'unauthenticated', firebase_id: firebaseId })
    navigate(loginURL())
  }

  const signInAsGuest = async (email: string) => {
    const cred = await signInAnonymously(auth)
    try {
      await updateEmail(cred?.user, email)
    } catch (e: any) {
      await cred?.user?.delete()
      if (e?.code === 'auth/email-already-in-use') {
        throw new Error('This email is already associated with an existing account. Please log in instead.')
      }
      throw e
    }
    return await refreshUser(true)
  }

  const linkCredentials = async (email: string, password: string) => {
    if (!auth.currentUser) return null
    const credential = EmailAuthProvider.credential(email, password)
    await linkWithCredential(auth.currentUser, credential)
    return await refreshUser(true)
  }

  // stream of User
  const authUser$ = React.useMemo(() => user(auth).pipe(
    switchMap(user => getUser(user)),
    shareReplay(1),
  ), [])

  // on page init, load user from firebase IndexedDB
  React.useEffect(() => {
    const sub = authUser$.subscribe(user => {
      if (authUser === undefined) setAuthUser(user)
    })
    return () => sub.unsubscribe()
  }, [])

  // send GTM event when user is authenticated
  React.useEffect(() => {
    if (!authUser?.firebaseId) return
    sendDataToGTM({ event: 'authenticated', firebase_id: authUser.firebaseId })
  }, [authUser?.firebaseId, sendDataToGTM])

  React.useEffect(() => {
    console.log('authUser', authUser)
  }, [authUser])

  const value = { authUser, refreshUser, logout, signInAsGuest, linkCredentials, authUser$ }
  return <AuthUserContext.Provider value={value}> {children} </AuthUserContext.Provider>
}

function useAuthUser() {
  const context = React.useContext(AuthUserContext)
  if (context === undefined) {
    throw new Error('useAuthUser must be used within a AuthUserProvider')
  }
  return context
}

export { AuthUserProvider, useAuthUser }